import api from "../api";

// Mock data for guardians forecasting
const getMockGuardiansForecastingData = () => {
  return {
    metrics: [
      { label: "Placed Guardians", value: 740, color: "#28a745" },
      { label: "Spaces in Under Offer", value: 97, color: "#ffc107" },
      { label: "Spaces in Maintenance", value: 75, color: "#fd7e14" },
      { label: "Current Available Spaces", value: 66, color: "#17a2b8" },
      { label: "Spaces in Vetting", value: 5105, color: "#6f42c1" },
      { label: "Potential Spaces", value: 5986, color: "#e83e8c" },
      { label: "Potential Guardians Forecast", value: 6083, color: "#20c997" },
    ],
    filters: {
      dateFrom: "2025-01-01",
      dateTo: "2025-01-31",
    },
    summary: {
      totalSpaces: 5986,
      totalGuardians: 740,
      utilizationRate: "87.6%",
    },
  };
};

// Generate PDF content for guardians forecasting
const generateGuardiansForecastingPDF = (data) => {
  let content = `Guardians Forecasting Report\nGenerated: ${new Date().toISOString()}\n\n`;
  content += `Date Range: ${data.filters.dateFrom} to ${data.filters.dateTo}\n\n`;
  content += `Summary:\n`;
  content += `Total Spaces: ${data.summary.totalSpaces}\n`;
  content += `Total Guardians: ${data.summary.totalGuardians}\n`;
  content += `Utilization Rate: ${data.summary.utilizationRate}\n\n`;
  content += `Forecasting Metrics:\n`;
  data.metrics.forEach((metric) => {
    content += `${metric.label}: ${metric.value}\n`;
  });
  return content;
};

const qualityService = {
  // Get guardians placed chart data (Laravel API)
  getGuardiansPlacedChartData: async (start, end, groupBy) => {
    try {
      console.log("Fetching guardians placed chart data:", {
        start,
        end,
        groupBy,
      });
      // Laravel route: /api/quality/guardians/placed
      // Backend route: /api/quality/guardians/placed (mounted at /api/quality, route is /guardians/placed)
      const response = await api.get("/quality/guardians/placed", {
        params: { start, end, "group-by": groupBy },
      });
      console.log("Guardians placed chart response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching guardians placed chart data:", error);
      throw error;
    }
  },

  // Get guardians placed data
  getGuardiansPlacedData: async (params = {}) => {
    try {
      console.log("Fetching guardians placed data:", params);
      const response = await api.get("/quality/guardians/placed", {
        params,
      });
      console.log("Guardians placed response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching guardians placed data:", error);
      console.log("Falling back to mock data");
      return {
        success: true,
        data: {
          placed: 740,
          underOffer: 97,
          inMaintenance: 75,
          vacant: 66,
          vetting: 5105,
          potentialSpaces: 5986,
          potentialGuardians: 6083,
        },
      };
    }
  },

  // Get guardians registered chart data (Laravel API)
  getGuardiansRegisteredChartData: async (start, end, groupBy) => {
    try {
      console.log("Fetching guardians registered chart data:", {
        start,
        end,
        groupBy,
      });
      // Laravel route: /api/quality/guardians/registered
      // Backend route: /api/quality/guardians/registered (mounted at /api/quality, route is /guardians/registered)
      const response = await api.get("/quality/guardians/registered", {
        params: { start, end, "group-by": groupBy },
      });
      console.log("Guardians registered chart response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching guardians registered chart data:", error);
      throw error;
    }
  },

  // Get guardians registered data
  getGuardiansRegisteredData: async (params = {}) => {
    try {
      console.log("Fetching guardians registered data:", params);
      const response = await api.get("/quality/guardians/registered", {
        params,
      });
      console.log("Guardians registered response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching guardians registered data:", error);
      console.log("Falling back to mock data");
      return {
        success: true,
        data: {
          registered: 5105,
          placed: 740,
          underOffer: 97,
          inMaintenance: 75,
          vacant: 66,
          potentialSpaces: 5986,
          potentialGuardians: 6083,
        },
      };
    }
  },

  // Export quality data (generic export function)
  exportQualityData: async (params = {}) => {
    try {
      console.log("Exporting quality data:", params);
      const response = await api.post("/quality/export", params, {
        responseType: "blob",
      });
      console.log("Quality export response received");
      return response.data;
    } catch (error) {
      console.error("Error exporting quality data:", error);
      console.log("Falling back to mock export");
      // Generate mock export content
      const content = `Quality Data Export\nGenerated: ${new Date().toISOString()}\n\nMock data for quality export`;
      return new Blob([content], { type: "application/pdf" });
    }
  },

  // Get guardians forecasting data
  getGuardiansForecasting: async (params = {}) => {
    try {
      console.log("Fetching guardians forecasting data:", params);
      const response = await api.get("/quality/guardians/forecasting", {
        params,
      });
      console.log("Guardians forecasting response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching guardians forecasting data:", error);
      console.log("Falling back to mock data");
      return {
        success: true,
        data: getMockGuardiansForecastingData(),
      };
    }
  },

  // Export guardians forecasting PDF
  exportGuardiansForecastingPDF: async (params = {}) => {
    try {
      console.log("Exporting guardians forecasting PDF:", params);
      const response = await api.get("/quality/guardians/forecasting/pdf", {
        params,
        responseType: "blob",
      });
      console.log("PDF export response received");
      return response.data;
    } catch (error) {
      console.error("Error exporting guardians forecasting PDF:", error);
      console.log("Falling back to mock PDF");
      // Generate mock PDF
      const data = getMockGuardiansForecastingData();
      const content = generateGuardiansForecastingPDF(data);
      return new Blob([content], { type: "application/pdf" });
    }
  },

  // Export guardians forecasting CSV
  exportGuardiansForecastingCSV: async (params = {}) => {
    try {
      console.log("Exporting guardians forecasting CSV:", params);
      const response = await api.post(
        "/quality/guardians/forecasting/export",
        {
          format: "csv",
          ...params,
        },
        {
          responseType: "blob",
        }
      );
      console.log("CSV export response received");
      return response.data;
    } catch (error) {
      console.error("Error exporting guardians forecasting CSV:", error);
      console.log("Falling back to mock CSV");
      // Generate mock CSV
      const data = getMockGuardiansForecastingData();
      let content = "Category,Value\n";
      data.metrics.forEach((metric) => {
        content += `${metric.label},${metric.value}\n`;
      });
      return new Blob([content], { type: "text/csv" });
    }
  },
};

// Export individual functions for backward compatibility
export const getGuardiansPlacedData = qualityService.getGuardiansPlacedData;
export const getGuardiansPlacedChartData =
  qualityService.getGuardiansPlacedChartData;
export const getGuardiansRegisteredData =
  qualityService.getGuardiansRegisteredData;
export const getGuardiansRegisteredChartData =
  qualityService.getGuardiansRegisteredChartData;
export const exportQualityData = qualityService.exportQualityData;

export default qualityService;
